import React, { useState, useEffect } from 'react';
import { Package, Plus, Truck, AlertTriangle, CheckCircle, X, Search, Filter, Edit3, Save } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const StockReplenishment = () => {
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Replenishment modal state
  const [showReplenishModal, setShowReplenishModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [replenishmentData, setReplenishmentData] = useState({
    quantity: '',
    supplier: '',
    unit_price: '',
    delivery_date: '',
    notes: '',
    reference_number: ''
  });
  
  // Batch replenishment
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchData, setBatchData] = useState({
    supplier: '',
    delivery_date: '',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchStockData();
    fetchCategories();
  }, []);

  const fetchStockData = async () => {
    try {
      setLoading(true);
      
      // Fetch out of stock items
      const outOfStockResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/out_of_stock/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Fetch low stock items
      const lowStockResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/low_stock/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      setOutOfStockItems(outOfStockResponse.data);
      setLowStockItems(lowStockResponse.data);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      alert('Error loading stock data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/categories/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const openReplenishModal = (item) => {
    setSelectedItem(item);
    setReplenishmentData({
      quantity: item.minimum_stock_level > 0 ? item.minimum_stock_level * 2 : 10,
      supplier: item.supplier || '',
      unit_price: item.unit_price || '',
      delivery_date: new Date().toISOString().split('T')[0],
      notes: '',
      reference_number: ''
    });
    setShowReplenishModal(true);
  };

  const handleReplenishment = async () => {
    if (!selectedItem || !replenishmentData.quantity) {
      alert('Please enter a quantity to replenish');
      return;
    }

    try {
      const quantity = parseInt(replenishmentData.quantity);
      if (quantity <= 0) {
        alert('Quantity must be greater than 0');
        return;
      }

      // Update stock using adjustment endpoint
      const adjustmentResponse = await axios.post(`${API_BASE_URL}/api/warehouse/items/${selectedItem.id}/adjust_stock/`, {
        adjustment: quantity,
        notes: `Stock replenishment: +${quantity} units. ${replenishmentData.notes || ''}`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      // Update item details if provided
      const updateData = {};
      if (replenishmentData.supplier && replenishmentData.supplier !== selectedItem.supplier) {
        updateData.supplier = replenishmentData.supplier;
      }
      if (replenishmentData.unit_price && parseFloat(replenishmentData.unit_price) !== selectedItem.unit_price) {
        updateData.unit_price = parseFloat(replenishmentData.unit_price);
      }

      if (Object.keys(updateData).length > 0) {
        await axios.patch(`${API_BASE_URL}/api/warehouse/items/${selectedItem.id}/`, updateData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
      }

      // Create stock movement record for delivery
      await axios.post(`${API_BASE_URL}/api/warehouse/stock-movements/`, {
        item: selectedItem.id,
        movement_type: 'stock_in',
        quantity: quantity,
        reference_number: replenishmentData.reference_number || `REP-${Date.now()}`,
        notes: `Delivery replenishment from ${replenishmentData.supplier || 'supplier'}. ${replenishmentData.notes || ''}`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      alert(`Successfully replenished ${selectedItem.name} with ${quantity} units!`);
      setShowReplenishModal(false);
      fetchStockData(); // Refresh data
    } catch (error) {
      console.error('Error replenishing stock:', error);
      alert('Error replenishing stock. Please try again.');
    }
  };

  const handleBatchReplenishment = async () => {
    if (selectedItems.length === 0) {
      alert('Please select items for batch replenishment');
      return;
    }

    try {
      const promises = selectedItems.map(async (itemId) => {
        const item = [...outOfStockItems, ...lowStockItems].find(i => i.id === itemId);
        const suggestedQuantity = item.minimum_stock_level > 0 ? item.minimum_stock_level * 2 : 10;
        
        // Adjust stock
        await axios.post(`${API_BASE_URL}/api/warehouse/items/${itemId}/adjust_stock/`, {
          adjustment: suggestedQuantity,
          notes: `Batch replenishment: +${suggestedQuantity} units. ${batchData.notes || ''}`
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        // Create stock movement
        await axios.post(`${API_BASE_URL}/api/warehouse/stock-movements/`, {
          item: itemId,
          movement_type: 'stock_in',
          quantity: suggestedQuantity,
          reference_number: batchData.reference_number || `BATCH-REP-${Date.now()}`,
          notes: `Batch delivery from ${batchData.supplier || 'supplier'}. ${batchData.notes || ''}`
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        return { itemId, quantity: suggestedQuantity, item: item.name };
      });

      const results = await Promise.all(promises);
      const totalItems = results.length;
      const totalQuantity = results.reduce((sum, r) => sum + r.quantity, 0);

      alert(`Successfully replenished ${totalItems} items with ${totalQuantity} total units!`);
      setShowBatchModal(false);
      setSelectedItems([]);
      fetchStockData(); // Refresh data
    } catch (error) {
      console.error('Error with batch replenishment:', error);
      alert('Error with batch replenishment. Some items may have been processed.');
    }
  };

  const filteredOutOfStock = outOfStockItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category_name === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredLowStock = lowStockItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category_name === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Package className="mr-3 h-8 w-8 text-blue-600" />
                Stock Replenishment
              </h1>
              <p className="text-gray-600 mt-2">Manage out-of-stock and low-stock items</p>
            </div>
            <div className="flex space-x-3">
              {selectedItems.length > 0 && (
                <button
                  onClick={() => setShowBatchModal(true)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Batch Replenish ({selectedItems.length})
                </button>
              )}
              <button
                onClick={fetchStockData}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Package className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-600 flex items-center">
              <span>Out of stock: {filteredOutOfStock.length} | Low stock: {filteredLowStock.length}</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-red-50 rounded-lg p-6 border border-red-200">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
              <div>
                <div className="text-2xl font-bold text-red-900">{filteredOutOfStock.length}</div>
                <div className="text-sm text-red-700">Out of Stock</div>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-yellow-600 mr-3" />
              <div>
                <div className="text-2xl font-bold text-yellow-900">{filteredLowStock.length}</div>
                <div className="text-sm text-yellow-700">Low Stock</div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-center">
              <Truck className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <div className="text-2xl font-bold text-blue-900">{selectedItems.length}</div>
                <div className="text-sm text-blue-700">Selected for Batch</div>
              </div>
            </div>
          </div>
        </div>

        {/* Out of Stock Items */}
        {filteredOutOfStock.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-red-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Out of Stock Items ({filteredOutOfStock.length})
            </h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...new Set([...selectedItems, ...filteredOutOfStock.map(i => i.id)])]);
                            } else {
                              setSelectedItems(selectedItems.filter(id => !filteredOutOfStock.map(i => i.id).includes(id)));
                            }
                          }}
                          className="w-4 h-4 text-red-600"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min. Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Supplier</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOutOfStock.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([...selectedItems, item.id]);
                              } else {
                                setSelectedItems(selectedItems.filter(id => id !== item.id));
                              }
                            }}
                            className="w-4 h-4 text-red-600"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.category_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.minimum_stock_level} {item.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.supplier || 'Not specified'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${parseFloat(item.unit_price || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openReplenishModal(item)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Replenish
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Low Stock Items */}
        {filteredLowStock.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-yellow-900 mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Low Stock Items ({filteredLowStock.length})
            </h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...new Set([...selectedItems, ...filteredLowStock.map(i => i.id)])]);
                            } else {
                              setSelectedItems(selectedItems.filter(id => !filteredLowStock.map(i => i.id).includes(id)));
                            }
                          }}
                          className="w-4 h-4 text-yellow-600"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min. Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shortage</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Supplier</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLowStock.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([...selectedItems, item.id]);
                              } else {
                                setSelectedItems(selectedItems.filter(id => id !== item.id));
                              }
                            }}
                            className="w-4 h-4 text-yellow-600"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-yellow-600">
                            {item.quantity_in_stock} {item.unit}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.minimum_stock_level} {item.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-red-600">
                            -{Math.max(0, item.minimum_stock_level - item.quantity_in_stock)} {item.unit}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.supplier || 'Not specified'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openReplenishModal(item)}
                            className="text-yellow-600 hover:text-yellow-900 flex items-center"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Replenish
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* No Items */}
        {filteredOutOfStock.length === 0 && filteredLowStock.length === 0 && (
          <div className="bg-green-50 rounded-lg border border-green-200 p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-green-900 mb-2">All Stock Levels are Good!</h3>
            <p className="text-green-700">No items are currently out of stock or below minimum levels.</p>
          </div>
        )}

        {/* Individual Replenishment Modal */}
        {showReplenishModal && selectedItem && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Replenish Stock - {selectedItem.name}
                  </h3>
                  <button
                    onClick={() => setShowReplenishModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700">Current Stock:</span>
                      <p className="font-medium">{selectedItem.quantity_in_stock || 0} {selectedItem.unit}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Minimum Level:</span>
                      <p className="font-medium">{selectedItem.minimum_stock_level} {selectedItem.unit}</p>
                    </div>
                    <div>
                      <span className="text-blue-700">Status:</span>
                      <p className={`font-medium ${selectedItem.stock_status === 'out_of_stock' ? 'text-red-600' : 'text-yellow-600'}`}>
                        {selectedItem.stock_status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock'}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-700">Current Supplier:</span>
                      <p className="font-medium">{selectedItem.supplier || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity to Add *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={replenishmentData.quantity}
                      onChange={(e) => setReplenishmentData({...replenishmentData, quantity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter quantity"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={replenishmentData.unit_price}
                      onChange={(e) => setReplenishmentData({...replenishmentData, unit_price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter unit price"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supplier
                    </label>
                    <input
                      type="text"
                      value={replenishmentData.supplier}
                      onChange={(e) => setReplenishmentData({...replenishmentData, supplier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter supplier name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={replenishmentData.reference_number}
                      onChange={(e) => setReplenishmentData({...replenishmentData, reference_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="PO number, invoice, etc."
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={replenishmentData.notes}
                    onChange={(e) => setReplenishmentData({...replenishmentData, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes about this replenishment..."
                  />
                </div>

                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-green-900 mb-2">After Replenishment:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm text-green-800">
                    <div>
                      <span>New Stock Level:</span>
                      <p className="font-medium">
                        {(selectedItem.quantity_in_stock || 0) + (parseInt(replenishmentData.quantity) || 0)} {selectedItem.unit}
                      </p>
                    </div>
                    <div>
                      <span>Total Cost:</span>
                      <p className="font-medium">
                        ${((parseFloat(replenishmentData.unit_price) || 0) * (parseInt(replenishmentData.quantity) || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowReplenishModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReplenishment}
                    disabled={!replenishmentData.quantity}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Add to Stock
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batch Replenishment Modal */}
        {showBatchModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Batch Replenishment ({selectedItems.length} items)
                  </h3>
                  <button
                    onClick={() => setShowBatchModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Supplier
                    </label>
                    <input
                      type="text"
                      value={batchData.supplier}
                      onChange={(e) => setBatchData({...batchData, supplier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Batch supplier name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={batchData.reference_number}
                      onChange={(e) => setBatchData({...batchData, reference_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Batch PO or invoice number"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch Notes
                  </label>
                  <textarea
                    value={batchData.notes}
                    onChange={(e) => setBatchData({...batchData, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notes for this batch replenishment..."
                  />
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-900 mb-3">Selected Items for Batch Replenishment:</h4>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-left">Current Stock</th>
                          <th className="px-3 py-2 text-left">Min Level</th>
                          <th className="px-3 py-2 text-left">Will Add</th>
                          <th className="px-3 py-2 text-left">New Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedItems.map(itemId => {
                          const item = [...outOfStockItems, ...lowStockItems].find(i => i.id === itemId);
                          const suggestedQuantity = item ? (item.minimum_stock_level > 0 ? item.minimum_stock_level * 2 : 10) : 0;
                          return (
                            <tr key={itemId} className="border-b border-blue-200">
                              <td className="px-3 py-2 font-medium">{item?.name || 'Unknown'}</td>
                              <td className="px-3 py-2">{item?.quantity_in_stock || 0}</td>
                              <td className="px-3 py-2">{item?.minimum_stock_level || 0}</td>
                              <td className="px-3 py-2 text-green-600 font-medium">+{suggestedQuantity}</td>
                              <td className="px-3 py-2 text-blue-600 font-medium">
                                {(item?.quantity_in_stock || 0) + suggestedQuantity}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
                    <p className="text-sm text-yellow-800">
                      <strong>Batch replenishment will use suggested quantities</strong> (2x minimum stock level or 10 units for items without minimum).
                      Each item will be restocked individually.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBatchModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBatchReplenishment}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Process Batch Replenishment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockReplenishment;